/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type NextApiRequest } from 'next';
import { getContentByContentTypeGraphQL } from '../../common/getContentByContentTypeGraphQL';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
// eslint-disable-next-line no-restricted-imports
import { getMostSpecificEntry } from '../utils/matchEnhancedPDPContent';
// eslint-disable-next-line no-restricted-imports
import { convertAnchorBlockContentToContentStackContent } from '@/features/contentstack/interface/contentTypeConverters';
// eslint-disable-next-line no-restricted-imports
import type { ContentStackContent } from '@/features/contentstack/interface/content-types';

export type ConvertedAnchor = {
  content: Partial<ContentStackContent>;
  modular_action: string | null;
  specificity: string | null;
  uid?: string;
};

export type ConvertedMatchedAnchors = {
  top: ConvertedAnchor | null;
  middle: ConvertedAnchor | null;
  bottom: ConvertedAnchor | null;
};

export function convertAnchor(
  anchorMatch: ReturnType<typeof getMostSpecificEntry>['top'],
): ConvertedAnchor | null {
  if (!anchorMatch) {
    return null;
  }
  return {
    content: convertAnchorBlockContentToContentStackContent(anchorMatch.anchor.content),
    modular_action: anchorMatch.anchor.modular_action,
    specificity: anchorMatch.specificity,
    uid: anchorMatch.uid,
  };
}

export const getContentStackEnhancedPDPContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
): Promise<{ response: ConvertedMatchedAnchors }> => {
  // Extract brand, sku, taxonomy from query params
  const brandName = query.brandName?.toString() || '';
  const skuId = query.sku?.toString() ?? '';
  const taxonomyPath = Array.isArray(query.taxonomyPath)
    ? `/${query.taxonomyPath.join('/')}`
    : (query.taxonomyPath ?? '');

  // Use GraphQL to fetch enhanced_pdp content with multiple filter combinations
  const graphqlResponse = await getContentByContentTypeGraphQL({
    brand: brandName,
    sku: skuId,
    taxonomy: taxonomyPath,
    req,
    query,
  });

  // Match most specific entry per anchor independently
  // (brand+sku > brand+taxonomy > brand only > taxonomy only)
  const matched = getMostSpecificEntry(graphqlResponse);

  return {
    response: {
      top: convertAnchor(matched.top),
      middle: convertAnchor(matched.middle),
      bottom: convertAnchor(matched.bottom),
    },
  };
};
